import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { Plus, X, ArrowLeft, PencilSimple } from "@phosphor-icons/react";
import OrgTree from "@/components/OrgTree";

export default function OrganizationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [structure, setStructure] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [addLevel, setAddLevel] = useState(null); // { level_number, parent_id, parent_label }
  const [editNode, setEditNode] = useState(null); // node object being edited
  const [editStructure, setEditStructure] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/organizations/${id}`);
      setStructure(r.data);
    } catch { toast.error("No se pudo cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [id]);
  useEffect(() => {
    api.get("/companies", { skipScope: true }).then(r => setCompanies(r.data || []));
    api.get("/installations", { skipScope: true }).then(r => setInstallations(r.data || []));
  }, []);

  const nodesByParent = useMemo(() => {
    const map = { root: [] };
    (structure?.nodes || []).forEach(n => {
      const key = n.parent_id || "root";
      if (!map[key]) map[key] = [];
      map[key].push(n);
    });
    Object.values(map).forEach(arr => arr.sort((a,b)=>(a.order_index||0)-(b.order_index||0) || a.label.localeCompare(b.label)));
    return map;
  }, [structure]);

  const levels = structure?.levels || [];
  const minLevel = levels[0]?.number;

  const handleDeleteNode = async (node) => {
    if (!window.confirm(`Eliminar "${node.label}" y todos sus descendientes?`)) return;
    try {
      await api.delete(`/organizations/nodes/${node.id}`);
      toast.success("Nodo eliminado");
      reload();
    } catch { toast.error("Error"); }
  };

  const handleMoveNode = async (node, direction) => {
    try {
      const r = await api.post(`/organizations/nodes/${node.id}/move?direction=${direction}`);
      if (r.data?.moved) {
        reload();
      } else if (r.data?.reason) {
        // silencioso en los extremos
      }
    } catch { toast.error("Error al mover"); }
  };

  const openAddFor = (level_number, parent_id = null, parent_label = null) => {
    setAddLevel({ level_number, parent_id, parent_label });
  };

  const toggleCollapse = (nid) => setCollapsed(prev => ({ ...prev, [nid]: !prev[nid] }));

  if (loading) return <div className="text-neutral-500">Cargando...</div>;
  if (!structure) return <div className="text-neutral-500">Estructura no encontrada</div>;

  return (
    <div className="space-y-6" data-testid="organization-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <Link to="/organizations" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800 mb-2" data-testid="back-to-organizations">
            <ArrowLeft size={16}/> Estructuras
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">{structure.name}</h1>
            <button onClick={()=>setEditStructure(true)} className="inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-800 border border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-md px-3 py-1 font-semibold" data-testid="edit-structure-btn">
              <PencilSimple size={14} weight="bold"/> Editar
            </button>
          </div>
          {structure.description && <p className="text-neutral-500 mt-1">{structure.description}</p>}
          <div className="flex flex-wrap gap-1 mt-3">
            {levels.map(l => (
              <span key={l.number} className="px-2 py-0.5 bg-neutral-100 text-neutral-700 text-xs rounded border border-neutral-200">
                L{l.number} · {l.label}
              </span>
            ))}
          </div>
        </div>
        <button onClick={()=>openAddFor(minLevel)} className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md" data-testid="add-root-node-btn">
          <Plus size={18} weight="bold"/> Agregar nodo raíz
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-md p-5" data-testid="structure-tree">
        {(nodesByParent.root || []).length === 0 ? (
          <div className="text-center py-10 text-neutral-500">
            <p className="font-semibold text-neutral-700 mb-1">Árbol vacío</p>
            <p className="text-sm">Agrega el primer nodo para empezar a construir la estructura.</p>
          </div>
        ) : (
          <OrgTree
            nodesByParent={nodesByParent}
            levels={levels}
            collapsed={collapsed}
            onToggle={toggleCollapse}
            onAdd={openAddFor}
            onEdit={(n)=>setEditNode(n)}
            onDelete={handleDeleteNode}
            onMove={handleMoveNode}
          />
        )}
      </div>

      {addLevel && (
        <AddNodeModal
          structure={structure}
          level_number={addLevel.level_number}
          parent_id={addLevel.parent_id}
          parent_label={addLevel.parent_label}
          companies={companies}
          installations={installations}
          onClose={()=>setAddLevel(null)}
          onCreated={()=>{ setAddLevel(null); reload(); }}
        />
      )}
      {editNode && (
        <EditNodeModal
          node={editNode}
          structure={structure}
          companies={companies}
          installations={installations}
          onClose={()=>setEditNode(null)}
          onSaved={()=>{ setEditNode(null); reload(); }}
        />
      )}
      {editStructure && (
        <EditStructureModal
          structure={structure}
          onClose={()=>setEditStructure(false)}
          onSaved={()=>{ setEditStructure(false); reload(); }}
        />
      )}
    </div>
  );
}

function EditStructureModal({ structure, onClose, onSaved }) {
  const [name, setName] = useState(structure.name || "");
  const [description, setDescription] = useState(structure.description || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Nombre requerido"); return; }
    setBusy(true);
    try {
      await api.patch(`/organizations/${structure.id}`, {
        name: name.trim(),
        description,
        level_numbers: (structure.levels || []).map(l => l.number),
      });
      toast.success("Estructura actualizada");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-structure-modal">
      <div className="bg-white rounded-md w-full max-w-lg border border-neutral-200 anim-fade-up">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-lg font-bold tracking-tight">Editar estructura</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre</label>
            <input value={name} onChange={e=>setName(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 rounded-md" data-testid="edit-structure-name"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-neutral-300 rounded-md" data-testid="edit-structure-desc"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white">Cancelar</button>
          <button onClick={submit} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="edit-structure-save">
            {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditNodeModal({ node, structure, companies, installations, onClose, onSaved }) {
  const levelDef = structure.levels.find(l => l.number === node.level_number);
  const [label, setLabel] = useState(node.label || "");
  const [refId, setRefId] = useState(node.ref_id || "");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const options = useMemo(() => {
    if (!levelDef?.ref_type) return [];
    const source = levelDef.ref_type === "company" ? companies : installations;
    const q = search.toLowerCase();
    return source.filter(i => !q
      || (i.name || "").toLowerCase().includes(q)
      || (i.code || "").toLowerCase().includes(q)
      || (i.tax_id || "").toLowerCase().includes(q));
  }, [levelDef, companies, installations, search]);

  const submit = async () => {
    const payload = {};
    if (levelDef?.ref_type) {
      if (!refId) { toast.error(`Selecciona ${levelDef.ref_type === "company" ? "una empresa" : "una sucursal"}`); return; }
      payload.ref_id = refId;
      if (label.trim() && label.trim() !== node.label) payload.label = label.trim();
    } else {
      if (!label.trim()) { toast.error("Nombre requerido"); return; }
      payload.label = label.trim();
    }
    setBusy(true);
    try {
      await api.patch(`/organizations/nodes/${node.id}`, payload);
      toast.success("Nodo actualizado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-node-modal">
      <div className="bg-white rounded-md w-full max-w-lg border border-neutral-200 anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-lg font-bold tracking-tight">
            Editar {levelDef?.label} <span className="text-neutral-400 text-sm font-normal">· L{node.level_number}</span>
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre</label>
            <input value={label} onChange={e=>setLabel(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 rounded-md" data-testid="edit-node-label"/>
          </div>
          {levelDef?.ref_type && (
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">
                {levelDef.ref_type === "company" ? "Empresa vinculada" : "Sucursal vinculada"}
              </label>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={levelDef.ref_type === "company" ? "Buscar empresa..." : "Buscar instalación..."} className="w-full px-3 py-2 border border-neutral-300 rounded-md mb-2" data-testid="edit-node-search"/>
              <div className="border border-neutral-200 rounded-md max-h-56 overflow-y-auto">
                {options.length === 0 ? (
                  <div className="p-4 text-sm text-neutral-500 text-center">Sin resultados</div>
                ) : options.map(opt => (
                  <label key={opt.id} className={`flex items-center gap-2 px-3 py-2 border-b border-neutral-100 cursor-pointer hover:bg-blue-50 ${refId === opt.id ? "bg-blue-50" : ""}`} data-testid={`edit-ref-${opt.id}`}>
                    <input type="radio" checked={refId === opt.id} onChange={()=>setRefId(opt.id)}/>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{opt.name}</div>
                      {(opt.tax_id || opt.code) && <div className="text-xs text-neutral-500">{opt.tax_id || opt.code}</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white">Cancelar</button>
          <button onClick={submit} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="edit-node-save">
            {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddNodeModal({ structure, level_number, parent_id, parent_label, companies, installations, onClose, onCreated }) {
  const levelDef = structure.levels.find(l => l.number === level_number);
  const [label, setLabel] = useState("");
  const [refId, setRefId] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  // Para sucursales, filtrar instalaciones por empresa del padre si existe
  const parentNode = useMemo(() => (structure.nodes || []).find(n => n.id === parent_id), [structure.nodes, parent_id]);
  const usedRefIds = useMemo(() => {
    // Evitar repetir el mismo ref dentro de la misma estructura
    return new Set((structure.nodes || []).filter(n => n.ref_type === levelDef?.ref_type && n.ref_id).map(n => n.ref_id));
  }, [structure.nodes, levelDef]);

  const options = useMemo(() => {
    if (levelDef?.ref_type === "company") {
      const list = companies.filter(c => !usedRefIds.has(c.id));
      if (!search) return list;
      const q = search.toLowerCase();
      return list.filter(c => (c.name || "").toLowerCase().includes(q) || (c.tax_id || "").toLowerCase().includes(q));
    }
    if (levelDef?.ref_type === "installation") {
      let list = installations.filter(i => !usedRefIds.has(i.id));
      // Si el padre es una Razón Social (company), filtrar por company_id
      if (parentNode?.ref_type === "company" && parentNode.ref_id) {
        list = list.filter(i => i.company_id === parentNode.ref_id);
      }
      if (!search) return list;
      const q = search.toLowerCase();
      return list.filter(i => (i.name || "").toLowerCase().includes(q) || (i.code || "").toLowerCase().includes(q));
    }
    return [];
  }, [levelDef, companies, installations, search, usedRefIds, parentNode]);

  const submit = async () => {
    const payload = { level_number, parent_id: parent_id || null };
    if (levelDef?.ref_type) {
      if (!refId) { toast.error(`Selecciona una ${levelDef.label}`); return; }
      payload.ref_id = refId;
    } else {
      if (!label.trim()) { toast.error("Nombre requerido"); return; }
      payload.label = label.trim();
    }
    setBusy(true);
    try {
      await api.post(`/organizations/${structure.id}/nodes`, payload);
      toast.success("Nodo agregado");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const title = parent_label ? `Agregar ${levelDef?.label} en "${parent_label}"` : `Agregar ${levelDef?.label}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="add-node-modal">
      <div className="bg-white rounded-md w-full max-w-lg border border-neutral-200 anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="add-node-close"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-4">
          {levelDef?.ref_type ? (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">
                  Seleccionar {levelDef.label}
                </label>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar por nombre o código..." className="w-full px-3 py-2 border border-neutral-300 rounded-md mb-2" data-testid="ref-search"/>
                <div className="border border-neutral-200 rounded-md max-h-60 overflow-y-auto" data-testid="ref-options">
                  {options.length === 0 ? (
                    <div className="p-4 text-sm text-neutral-500 text-center">
                      {parentNode?.ref_type === "company" && levelDef.ref_type === "installation"
                        ? "No hay sucursales disponibles para esta Razón Social."
                        : "No hay registros disponibles (o todos ya están asignados)."}
                    </div>
                  ) : options.map(opt => (
                    <label key={opt.id} className={`flex items-center gap-2 px-3 py-2 border-b border-neutral-100 cursor-pointer hover:bg-blue-50 ${refId === opt.id ? "bg-blue-50" : ""}`} data-testid={`ref-option-${opt.id}`}>
                      <input type="radio" checked={refId === opt.id} onChange={()=>setRefId(opt.id)}/>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{opt.name}</div>
                        {(opt.tax_id || opt.code) && <div className="text-xs text-neutral-500">{opt.tax_id || opt.code}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre del {levelDef?.label}</label>
              <input value={label} onChange={e=>setLabel(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 rounded-md" placeholder={`Ej. ${levelDef?.label}`} data-testid="node-label-input"/>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white">Cancelar</button>
          <button onClick={submit} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="add-node-submit">
            {busy ? "Agregando..." : "Agregar"}
          </button>
        </div>
      </div>
    </div>
  );
}
